import React, { useContext, useState } from 'react';
import { appendQueryParamsForCheckout, formatCheckoutData, log } from '../../utils/logs';

import { Button } from '../common';
import LazyLoad from 'react-lazyload';
import { Link } from 'gatsby';
import { Rating } from '../Rating';
import { StoreContext } from '../../store';
import { colors } from '../../utils/styles';
import styled from 'styled-components';

export const DealCard = ({ product, quickBuyEnabled = true }) => {
  const { quickBuy } = useContext(StoreContext);
  const [isLoading, setIsLoading] = useState(false);
  const variant = product.shopify_product.variants[0];
  const onBtnClick = async () => {
    try {
      setIsLoading(true);
      const checkout = await quickBuy(variant.storefrontId);
      log('add_to_cart', {
        id: product.shopify_product.id,
        name: product.shopify_product.title,
        variant: variant.title,
        quantity: 1,
        price: variant.price,
      });
      log('begin_checkout', formatCheckoutData(checkout));
      window.location.href = appendQueryParamsForCheckout(checkout.webUrl);
    } catch (error) {
      window.Sentry.captureException(error);
      alert('An error occured!');
      setIsLoading(false);
    }
  };

  return (
    <Card
      backgroundColor={product.product_card_background}
      className={product.out_of_stock ? '--out_of_stock' : ''}
    >
      <Badge>SALE -{variant.compareAtPrice - variant.price}$</Badge>
      <Thumbnail once>
        <img
          srcSet={product.image.srcSet}
          src={product.image.src}
          alt={product.image.alt || product.title}
          height="307"
        />
      </Thumbnail>
      <Content>
        <Header>
          <div>
            <Heading>{product.title}</Heading>
            <Rating total={product.reviews_count} rate={product.reviews_stars} />
          </div>
          <Prices>
            <span>${variant.compareAtPrice}</span>
            <strong>${variant.price}</strong>
          </Prices>
        </Header>
        <Description>{product.product_card_description}</Description>
        {quickBuyEnabled ? (
          <StyledButton
            isLoading={isLoading}
            className={product.out_of_stock ? '--inactive' : '--active'}
            disabled={isLoading || product.out_of_stock}
            onClick={onBtnClick}
            $isGrayscaled={product.out_of_stock}
          >
            {product.out_of_stock ? 'SOLD OUT' : 'Quick Buy'}
          </StyledButton>
        ) : (
          <StyledButton
            className={product.out_of_stock ? '--inactive' : '--active'}
            as={product.out_of_stock ? 'div' : Link}
            to={`/products/${product.uid}`}
            $isGrayscaled={product.out_of_stock}
          >
            {product.out_of_stock ? 'SOLD OUT' : 'Show Now'}
          </StyledButton>
        )}
      </Content>
    </Card>
  );
};

const Badge = styled.span`
  position: absolute;
  left: 20px;
  color: #ffffff;
  top: 20px;
  width: 108px;
  height: 37px;
  display: flex;
  font-weight: 500;
  font-size: 13px;
  align-items: center;
  justify-content: center;
  background: ${colors.pink};
  border-radius: 2px;
`;

const Prices = styled.span`
  display: flex;
  flex-direction: column;
  span {
    text-decoration: line-through;
  }
  strong {
    font-weight: bold;
    text-decoration: none;
    margin-top: 5px;
    color: ${colors.pink};
  }
`;

const Header = styled.div`
  display: flex;
  > div {
    flex: 1;
    margin-right: 30px;
  }
`;

const StyledButton = styled(Button)`
  width: 100%;
  margin-top: auto;
  &.--inactive {
    background: transparent;
    border: 2px solid #141414;
    color: #141414;
    opacity: 0.8;
  }
`;

const Thumbnail = styled(LazyLoad)`
  height: 350px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #c2c2bc;
  padding: 20px;
`;

const Heading = styled.h3`
  font-weight: 600;
  font-size: 16px;
  line-height: 19px;
  width: 100%;
  margin-bottom: 5px;
`;

const Description = styled.p`
  font-weight: 300;
  font-size: 13px;
  line-height: 131.5%;
  margin: 0 0 12px;
  width: 100%;
`;

const Content = styled.div`
  padding: 13px 30px 18px;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Card = styled.article`
  background: ${p => p.backgroundColor};
  display: flex;
  flex-direction: column;
  position: relative;
`;
